import { Component, OnInit, Input, Output, EventEmitter, ViewChild, OnChanges, SimpleChange, SimpleChanges, OnDestroy } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, RouterPreloader } from '@angular/router';
import { Subscription } from 'rxjs';
import { BinderClassification } from 'src/app/models/binder/binder-classification.model';
import { BinderContent, BinderTabContent, BinderTabDocument } from 'src/app/models/binder/binder-content.model';
import { BinderDetails } from 'src/app/models/binder/binder-details.model';
import { BinderDocuments } from 'src/app/models/binder/binder-documentation.model';
import { TokenHelper } from 'src/app/services/helpers/token.helper';
import { BinderWizardService } from 'src/app/services/sections/binder/binder-wizard.service';
import { ToastService } from 'src/app/services/ui/toast.service';
import { FormArrayValidators } from 'src/app/validators/form-array.validator';
import { GenericModalComponent } from '../../generic-modal/generic-modal.component';
import { ToastType } from '../../toast/models/toast-type.enum';
import { Toast } from '../../toast/models/toast.model';
import {BinderFilter} from '../../../models/binder/binder-filter.model';

@Component({
	selector: 'binder-send-documentation',
	templateUrl: './binder-send-documentation.component.html',
	styleUrls: ['./binder-send-documentation.component.scss',
		'../../stylesheets/job-actions.scss',
		'../../stylesheets/job-main.scss',
		'../../stylesheets/job-inputs.scss',
		'../../stylesheets/forms-container.scss']
})
export class BinderSendDocumentationComponent implements OnInit, OnChanges{
	@ViewChild('tabList', {static: true}) public modal: GenericModalComponent;
	@Input() public years: number[] = null;
	@Input() public binderParents: BinderClassification[] = null;
	@Input() public selectedBinderChildren: BinderClassification[] = null;
	@Input() public binderDetails: BinderDetails = null;
	@Input() public targetEmail: string = null;
	@Input() public selectedYear: number = null;
	@Input() public binderClassification: BinderClassification[] = null;
	@Input() public binderContent: BinderContent = null;

	// tslint:disable-next-line:no-output-on-prefix
	@Output() public onClose: EventEmitter<void> = new EventEmitter<void>();

	public selectedBinderTabsForm: FormGroup;
	public isDataLoading = false;
	public isTabListEmpty = false;
	public isVisible = false;

	public tabs = new Map<number, number[]>();

	constructor(
		private activatedRoute: ActivatedRoute,
		private binderService: BinderWizardService
	) { }

	ngOnInit(): void {
		this.initializeTabsForm();
		this.initializeTabsStructure();

		this.selectedBinderTabsForm.get('year').valueChanges.subscribe((year) => {
			if (this.isVisible) {
				this.getBinderClassificationDocuments(year);
			}
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (!!changes.selectedYear && changes.selectedYear.currentValue !== changes.selectedYear.previousValue && !!this.selectedBinderTabsForm) {
			this.selectedBinderTabsForm.patchValue({year: changes.selectedYear.currentValue});
		}
		if (!!changes.binderContent && !!changes.binderContent.currentValue) {
			this.filterBinderContent(this.binderContent);
		}
	}

	get binderClassificationIds(): FormArray {
		return this.selectedBinderTabsForm.get('binderClassifications') as FormArray;
	}

	public initializeTabsForm(): void {
		this.selectedBinderTabsForm = new FormGroup({
			binderClassifications: new FormArray([], FormArrayValidators.atLeastOneItem()),
			year: new FormControl(this.selectedYear, Validators.required)
		});
	}
	public sendDocuments(): void {
		this.isDataLoading = true;
		const model = this.selectedBinderTabsForm.getRawValue();

		const data = model.binderClassifications.filter(t => t.value).map(t => {
			const tabDetails = this.getTabDetails(t.binderClassificationId);
			let groupName = tabDetails.binderName;

			if (!!tabDetails?.parentBinderClassificationId) {
				groupName = `${this.getTabDetails(tabDetails?.parentBinderClassificationId).binderName} - ${groupName}`;
			}
			return {
				binderClassificationId: t.binderClassificationId,
				documents: t.documents,
				groupName
			};
		});
		this.binderService.submitDocumentation(
			TokenHelper.getToken(this.activatedRoute),
			data,
			this.selectedBinderTabsForm.get('year').value
			)
			.subscribe(() => {
				this.modal.hide();
				this.isDataLoading = false;
			}, () => {
				ToastService.pushToast({
					icon: 'error',
					text: 'The request could not be completed! Please try again',
					type: ToastType.Error
				} as Toast);
				this.isDataLoading = false;
			});
	}

	public reset(): void {
		if (!!this.selectedBinderTabsForm) {
			this.binderClassificationIds.controls.forEach(group => {
				group.patchValue({value: false});
			});
		}
	}

	public open(): void {
		this.isVisible = true;
		this.reset();
		this.modal.open();
	}

	public close(): void {
		this.isVisible = false;
		this.modal.hide();
	}

	public initializeTabsStructure(): void {
		this.binderClassification.filter(t => !t.parentBinderClassificationId).forEach((tab) => {
			this.tabs.set(tab.binderClassificationId, []);
		});
		this.binderClassification.filter(t => !!t.parentBinderClassificationId).forEach((item) => {
			if (
				this.tabs.has(item.parentBinderClassificationId)
			) {
				this.tabs.get(item.parentBinderClassificationId).push(item.binderClassificationId);
			}
		});
		this.intializeTabsFormArray();
	}

	public getTabDetails(binderClassificationId: number): BinderClassification {
		return this.binderClassification.find(t => t.binderClassificationId === binderClassificationId);
	}

	public getBinderClassificationDocuments(year: number): void {
		if (!year) {
			return;
		}
		const binderFilter: BinderFilter = {year};
		this.isDataLoading = true;
		this.binderService.getBinderClassificationDocuments(
			TokenHelper.getToken(this.activatedRoute),
			binderFilter
		).subscribe(
			(content: BinderContent) => {
				this.filterBinderContent(content);
				this.binderContent = content;
				this.isDataLoading = false;
			}, () => {
				this.isDataLoading = false;
			}
		);
	}

	public intializeTabsFormArray(): void {
		this.binderClassificationIds.clear();
		this.tabs.forEach((children, parent) => {
			const tab = this.getTabDetails(parent);
			this.binderClassificationIds.push(
				new FormGroup({
				binderClassificationId: new FormControl(
					tab.binderClassificationId,
					Validators.required
				),
				value: new FormControl(
					{ value: false, disabled: true },
					Validators.required
				),
				documents: new FormControl([]),
				isParent: new FormControl(true),
				isVisible: new FormControl(true)
			}));
			children.forEach(child => {
				const item = this.getTabDetails(child);
				if (!!item) {
					this.binderClassificationIds.push(
						new FormGroup({
						binderClassificationId: new FormControl(
							item.binderClassificationId,
							Validators.required
						),
						value: new FormControl(
							{ value: false, disabled: true },
							Validators.required
						),
						documents: new FormControl([]),
						isParent: new FormControl(false),
						isVisible: new FormControl(true)
					}));
				}
			});
		});
	}

	public filterBinderContent(content: BinderContent): void {
		this.binderClassificationIds.controls.forEach((group: FormGroup) => {
			const binderClassificationId = group.get('binderClassificationId').value;
			const tabContent = content.tabs.find(t => t.binderClassificationId === binderClassificationId);
			const binderTabDetails = this.binderClassification.find(t => t.binderClassificationId === binderClassificationId);
			group.patchValue({
				documents: tabContent?.documents ?? []
			});

			if (!binderTabDetails.parentBinderClassificationId) {
				const isVisibleVar = binderTabDetails?.children?.some((child: BinderClassification) => {
					return content.tabs.find(t => t.binderClassificationId === child.binderClassificationId)?.documents;
				}
				);
				group.patchValue({
					isVisible: (!!tabContent?.documents || isVisibleVar) ? true :  false
				});
			} else {
				group.patchValue({
					isVisible: (!!tabContent?.documents) ? true :  false
				});
			}
			if (!!tabContent) {
				group.get('value').enable();
			} else {
				group.get('value').disable();
				group.patchValue({
					value: false
				});
			}
		});
		this.isTabListEmpty = this.binderClassificationIds.controls.every((group: FormGroup) =>
			group.get('isVisible').value === false
		);
	}
}
